"use client";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import { History, Zap, Clock, Award } from "lucide-react";

interface BenefitCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function BenefitCard({ icon, title, description }: BenefitCardProps) {
  return (
    <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {title}
      </h3>
      <p className="text-gray-600 text-sm leading-relaxed">
        {description}
      </p>
    </div>
  );
}

export default function WhyChooseConfy() {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: <History className="w-6 h-6" />,
      titleKey: 'home.whyChoose.mozAccreditation.title',
      descriptionKey: 'home.whyChoose.mozAccreditation.description',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      titleKey: 'home.whyChoose.expertInstructors.title',
      descriptionKey: 'home.whyChoose.expertInstructors.description',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      titleKey: 'home.whyChoose.flexibleLearning.title',
      descriptionKey: 'home.whyChoose.flexibleLearning.description',
    },
    {
      icon: <Award className="w-6 h-6" />,
      titleKey: 'home.whyChoose.cpdCredits.title',
      descriptionKey: 'home.whyChoose.cpdCredits.description',
    },
  ];

  return (
    <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            {t('home.whyChoose.title')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('home.whyChoose.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              icon={benefit.icon}
              title={t(benefit.titleKey)}
              description={t(benefit.descriptionKey)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
